import { Weaken } from 'utils/reusable-types';
import { i18n } from 'i18n';
import {
  BreakingTestDto,
  BreakingTestForm,
  CylinderData,
  PagedListOfPourSummaryDto
} from './api';
import {
  makeCreateMutation,
  makeGetQuery,
  makeListQuery,
  makeUpdateMutation,
  PageParams
} from './useApi';
import { BREAKING_SCHEDULE_KEY, BREAKING_SCHEDULES_KEY } from './useBreakingSchedules';

// Export the types from the generated API
export type {
  BreakingTestForm,
  CylinderData,
};

export type BreakingTestInputs = Weaken<BreakingTestForm>;

const BREAKING_TESTS_KEY = 'breaking-tests';
const BREAKING_TEST_KEY = 'breaking-test';

export const useGetBreakingTestQuery = makeGetQuery<BreakingTestDto, string>(
  BREAKING_TEST_KEY,
  (api, breakingTestId) => () => api.breakingTests.get(breakingTestId),
);

export interface QAQCPoursSearchParameters extends PageParams {
  pourReferenceSearch?: string;
  projectId?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export const useListAllPoursForQAQCUseQuery = makeListQuery<QAQCPoursSearchParameters, PagedListOfPourSummaryDto>(
  BREAKING_TESTS_KEY,
  (api, params) => () => api.breakingTests.listAllPoursForQAQCUser(
    params.page,
    params.pageSize,
    params.startDate,
    params.endDate,
    params.pourReferenceSearch,
    params.projectId,
    params.sortBy,
    params.sortDirection,
  ),
);

export interface BreakingTestMutation {
  breakingScheduleId: string;
  breakingTestForm: BreakingTestForm;
}

// NOTE: Using updated API that accepts form object
export const useCreateBreakingTestMutation = makeCreateMutation<void, BreakingTestMutation>(
  BREAKING_TESTS_KEY,
  (api) => (breakingTestMutation) => api.breakingTests.create(
    breakingTestMutation.breakingScheduleId,
    breakingTestMutation.breakingTestForm,
  ),
  () => i18n._('The cylinder breaking test has been saved'),
  [BREAKING_TEST_KEY, BREAKING_SCHEDULES_KEY, BREAKING_SCHEDULE_KEY],
);

export const useUpdateBreakingTestMutation = makeUpdateMutation<void, BreakingTestMutation>(
  BREAKING_TEST_KEY,
  BREAKING_TESTS_KEY,
  (api, breakingTestId) => (breakingTestMutation) => api.breakingTests.update(
    breakingTestId,
    breakingTestMutation.breakingTestForm,
  ),
  () => i18n._('The cylinder breaking test has been updated'),
  [BREAKING_TEST_KEY, BREAKING_SCHEDULES_KEY, BREAKING_SCHEDULE_KEY],
);
